import * as React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { CHAT_LAYOUT } from '@/config/layout'
import { flattenLabels, type LabelConfig } from '@craft-agent/shared/labels'
import { getConnectionModelLabel, resolveEffectiveConnectionSlug } from '@config/llm-connections'
import { AGENTS_PLUGIN_NAME } from '@craft-agent/shared/skills/types'
import type { PermissionMode } from '@craft-agent/shared/agent/modes'
import type { SessionStatus } from '@/config/session-status-config'
import { useOptionalAppShellContext } from '@/context/AppShellContext'
import type { BackgroundTask } from '../ActiveTasksBar'
import { ActiveOptionBadges } from '../ActiveOptionBadges'
import { InputContainer } from './InputContainer'
import type { LoadedSkill } from '@/shared/types'

interface ExampleCard {
  id: string
  title: string
  description: string
  prompt: string
  sourceSlugs: string[]
  skillSlugs: string[]
}

const EXAMPLE_CARDS: ExampleCard[] = [
  {
    id: 'sentry-bug',
    title: 'Investigate a Sentry bug',
    description: 'Trace errors, find root causes, and suggest fixes',
    prompt: 'Investigate this Sentry issue: [paste Sentry URL here]\n\nMy dev folder is at: [paste path to your codebase]',
    sourceSlugs: ['devin', 'github', 'sentry'],
    skillSlugs: ['bug-investigator'],
  },
  {
    id: 'prd',
    title: 'Transform an idea into a PRD',
    description: 'Structure your thinking into a clear product spec',
    prompt: 'Help me write a PRD for the following idea:\n\n[describe your idea here]',
    sourceSlugs: ['notion', 'perplexity'],
    skillSlugs: ['prd-creator', 'docx'],
  },
  {
    id: 'granola',
    title: 'Check Granola action items',
    description: 'Review meetings from last week and extract all action items',
    prompt: 'Check Granola for all my action items from all meetings last week. Organize them by meeting and priority.',
    sourceSlugs: ['google-calendar', 'granola'],
    skillSlugs: [],
  },
  {
    id: 'linear',
    title: 'Track Linear project status',
    description: 'Get a snapshot of all ongoing project progress',
    prompt: 'Check the status of all ongoing projects on Linear. Summarize progress, blockers, and what needs attention.',
    sourceSlugs: ['linear', 'slack'],
    skillSlugs: [],
  },
  {
    id: 'zendesk',
    title: 'Analyze ZenDesk feedback',
    description: 'Surface common complaints and suggestions from the last 30 days',
    prompt: 'What are the most common complaints or suggestions coming from ZenDesk in the last 30 days? Categorize them and rank by frequency.',
    sourceSlugs: ['zendesk'],
    skillSlugs: [],
  },
  {
    id: 'slack-summary',
    title: 'Summarize Slack messages',
    description: 'Catch up on important conversations from the last couple of days',
    prompt: 'Check all my Slack messages from the last couple of days and summarize everything important. Group by channel and highlight action items or decisions that need my attention.',
    sourceSlugs: ['slack'],
    skillSlugs: [],
  },
]

function getTimeOfDayGreeting(firstName?: string): { title: string; subtitle: string } {
  const hour = new Date().getHours()
  const subtitle = 'Your command center for asking, automating, and accelerating anything.'
  const name = firstName ? `, ${firstName}` : ''
  if (hour >= 5 && hour < 12) return { title: `Good morning${name} — let's get to work.`, subtitle }
  if (hour >= 12 && hour < 17) return { title: `Good afternoon${name} — what's next?`, subtitle }
  if (hour >= 17 && hour < 21) return { title: `Good evening${name} — let's wrap things up.`, subtitle }
  return { title: `Still at it${name}? Let's make it count.`, subtitle }
}

interface ChatInputZoneProps {
  compactMode?: boolean
  showOptionBadges?: boolean
  permissionMode?: PermissionMode
  onPermissionModeChange?: (mode: PermissionMode) => void
  tasks?: BackgroundTask[]
  sessionId: string
  sessionFolderPath?: string
  onKillTask?: (taskId: string) => void
  onInsertMessage?: (text: string) => void
  inputValue?: string
  sessionLabels?: string[]
  labels?: LabelConfig[]
  onLabelsChange?: (labels: string[]) => void
  skills?: LoadedSkill[]
  workspaceId?: string
  sessionStatuses?: SessionStatus[]
  currentSessionStatus?: string
  onSessionStatusChange?: (stateId: string) => void
  isEmptySession?: boolean
  onSourcesChange?: (slugs: string[]) => void
  /** Whether non-empty UI (badges) should be visible — managed by parent for sequenced transitions */
  showNonEmptyUI?: boolean
  /** Fired when the empty state exit animation finishes */
  onEmptyStateExitComplete?: () => void
  className?: string
  inputProps: React.ComponentProps<typeof InputContainer>
}

export function ChatInputZone({
  compactMode = false,
  showOptionBadges,
  permissionMode = 'allow-all',
  onPermissionModeChange,
  tasks = [],
  sessionId,
  sessionFolderPath,
  onKillTask,
  onInsertMessage,
  inputValue = '',
  sessionLabels = [],
  labels = [],
  onLabelsChange,
  skills = [],
  workspaceId,
  sessionStatuses = [],
  currentSessionStatus = 'todo',
  onSessionStatusChange,
  isEmptySession = false,
  onSourcesChange,
  showNonEmptyUI = false,
  onEmptyStateExitComplete,
  className,
  inputProps,
}: ChatInputZoneProps) {
  const [autoOpenLabelId, setAutoOpenLabelId] = React.useState<string | null>(null)
  const appShellContext = useOptionalAppShellContext()
  const llmConnections = appShellContext?.llmConnections ?? []
  const effectiveConnectionSlug = resolveEffectiveConnectionSlug(
    inputProps.currentConnection,
    appShellContext?.workspaceDefaultLlmConnection,
    llmConnections,
  )
  const effectiveConnection = llmConnections.find(connection => connection.slug === effectiveConnectionSlug)
  const currentModelLabel = getConnectionModelLabel(inputProps.currentModel, effectiveConnection)
  const shouldShowOptionBadges = showOptionBadges ?? !compactMode
  const [greeting, setGreeting] = React.useState(() => getTimeOfDayGreeting())
  const showEmptyState = !compactMode && isEmptySession && !showNonEmptyUI

  React.useEffect(() => {
    window.electronAPI?.readPreferences().then(({ content }: { content: string }) => {
      try {
        const prefs = JSON.parse(content)
        const firstName = prefs.name?.split(' ')[0]
        if (firstName) setGreeting(getTimeOfDayGreeting(firstName))
      } catch { /* ignore */ }
    }).catch(() => { /* ignore — may fail during reconnect */ })
  }, [])

  const handleLabelAdd = React.useCallback((labelId: string) => {
    const current = sessionLabels || []
    if (current.includes(labelId)) return

    onLabelsChange?.([...current, labelId])

    const config = flattenLabels(labels || []).find(label => label.id === labelId)
    if (config?.valueType) {
      setAutoOpenLabelId(labelId)
    }
  }, [labels, onLabelsChange, sessionLabels])

  const handleExampleCardClick = React.useCallback((card: ExampleCard) => {
    let text = card.prompt
    if (card.skillSlugs.length > 0) {
      const skillMentions = card.skillSlugs
        .map(slug => `[skill:${AGENTS_PLUGIN_NAME}:${slug}]`)
        .join(' ')
      text = text + '\n\n' + skillMentions + ' '
    }
    inputProps.onInputChange?.(text)
    if (card.sourceSlugs.length > 0) {
      onSourcesChange?.(card.sourceSlugs)
    }
    onPermissionModeChange?.('allow-all')
  }, [inputProps.onInputChange, onSourcesChange, onPermissionModeChange])

  return (
    <div
      className={cn(CHAT_LAYOUT.maxWidth, 'keyboard-safe-area-bottom mx-auto w-full px-4 mt-1 pb-4', className)}
    >
      <AnimatePresence onExitComplete={onEmptyStateExitComplete}>
        {showEmptyState && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="flex items-end justify-between pt-6 pb-4 select-none"
          >
            <div className="min-w-0">
              <h1 className="font-semibold tracking-tight text-foreground" style={{ fontSize: 18 }}>
                {greeting.title}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {greeting.subtitle}
              </p>
            </div>
            {shouldShowOptionBadges && (
              <ActiveOptionBadges
                className="!mb-0 !pb-0 shrink-0"
                permissionMode={permissionMode}
                onPermissionModeChange={onPermissionModeChange}
                tasks={tasks}
                sessionId={sessionId}
                sessionFolderPath={sessionFolderPath}
                onKillTask={onKillTask}
                onInsertMessage={onInsertMessage ?? inputProps.onInputChange}
                inputValue={inputValue}
                sessionLabels={sessionLabels}
                labels={labels}
                onLabelsChange={onLabelsChange}
                skills={skills}
                workspaceId={workspaceId}
                onRemoveLabel={(labelId) => {
                  const next = (sessionLabels || []).filter(entry => entry !== labelId && !entry.startsWith(`${labelId}::`))
                  onLabelsChange?.(next)
                }}
                autoOpenLabelId={autoOpenLabelId}
                onAutoOpenConsumed={() => setAutoOpenLabelId(null)}
                sessionStatuses={sessionStatuses}
                currentSessionStatus={currentSessionStatus}
                onSessionStatusChange={onSessionStatusChange}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!showEmptyState && showNonEmptyUI && shouldShowOptionBadges && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <ActiveOptionBadges
              permissionMode={permissionMode}
              onPermissionModeChange={onPermissionModeChange}
              tasks={tasks}
              sessionId={sessionId}
              sessionFolderPath={sessionFolderPath}
              onKillTask={onKillTask}
              onInsertMessage={onInsertMessage ?? inputProps.onInputChange}
              inputValue={inputValue}
              sessionLabels={sessionLabels}
              labels={labels}
              onLabelsChange={onLabelsChange}
              skills={skills}
              workspaceId={workspaceId}
              onRemoveLabel={(labelId) => {
                const next = (sessionLabels || []).filter(entry => entry !== labelId && !entry.startsWith(`${labelId}::`))
                onLabelsChange?.(next)
              }}
              autoOpenLabelId={autoOpenLabelId}
              onAutoOpenConsumed={() => setAutoOpenLabelId(null)}
              sessionStatuses={sessionStatuses}
              currentSessionStatus={currentSessionStatus}
              onSessionStatusChange={onSessionStatusChange}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div layout transition={{ layout: { duration: showEmptyState ? 0 : 0.4, ease: 'easeInOut' } }}>
        <InputContainer
          {...inputProps}
          compactMode={compactMode}
          permissionMode={permissionMode}
          onPermissionModeChange={onPermissionModeChange}
          labels={labels}
          sessionLabels={sessionLabels}
          onLabelAdd={handleLabelAdd}
          sessionFolderPath={sessionFolderPath}
          sessionId={sessionId}
          currentSessionStatus={currentSessionStatus}
        />

        {!compactMode && (
          <div className="text-center mt-2 select-none">
            <span className="text-xs text-muted-foreground/50">
              {currentModelLabel}
            </span>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {showEmptyState && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="mt-10"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider select-none">
                Need inspiration?
              </span>
              <div className="flex-1 h-px bg-border/50" />
            </div>
            <div className="grid grid-cols-3 gap-3 pb-12">
              {EXAMPLE_CARDS.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleExampleCardClick(card)}
                  className={cn(
                    'text-left p-3 rounded-xl border border-border/50 bg-background',
                    'hover:bg-foreground/3 hover:border-border transition-colors',
                    'group cursor-pointer select-none'
                  )}
                >
                  <div className="text-[13px] font-semibold text-foreground/80 group-hover:text-foreground transition-colors">
                    {card.title}
                  </div>
                  <div className="text-[13px] text-muted-foreground/80 mt-1 line-clamp-2">
                    {card.description}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
